// src/admin/UserManagementPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
// --- MODIFICATION: Import the new API function and Modal ---
import { getAllUsers, updateUser, deleteUser, updateUserSpins, getUserInventoryById, updateUserType } from '../api'; // Added updateUserType
import UserInventoryModal from './UserInventoryModal'; // Import the new modal
import { useAuth } from '../AuthContext';
import { useSnackbar } from 'notistack'; // Assuming you use notistack for feedback

// --- Material UI Imports ---
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Link from '@mui/material/Link'; // Added for Steam profile link

// --- Material UI Icons ---
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import InventoryIcon from '@mui/icons-material/Inventory'; // <-- Import Inventory icon

// --- Helper Functions (Optional: Move to a utils file) ---
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        return new Date(dateString).toLocaleString();
    } catch (e) {
        console.error("Error formatting date:", e);
        return 'Invalid Date';
    }
};

// --- Edit Modal Component (UserEditModal) ---
function UserEditModal({ open, onClose, user, onSaveSuccess }) {
    const [originalUserData, setOriginalUserData] = useState(null);
    const [formData, setFormData] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState('');

    useEffect(() => {
        if (user) {
            const initialData = {
                userId: user.userId,
                steamId: user.steamId || '',
                displayName: user.displayName || '',
                steamTradeUrl: user.steamTradeUrl || '',
                spins: user.spins || 0,
                referralCode: user.referralCode || '',
                referredByUserId: user.referredByUserId || '',
                referralBonusClaimed: user.referralBonusClaimed || false,
                spinsFromReferrals: user.spinsFromReferrals || 0,
                // userType is handled separately in the grid now
            };
            setFormData(initialData);
            setOriginalUserData(user);
            setSaveError('');
        } else {
            setFormData({});
            setOriginalUserData(null);
        }
    }, [user, open]);

    const handleChange = (event) => {
        const { name, value, type } = event.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'number' ? parseInt(value, 10) || 0 : value,
        }));
    };

    const handleSave = async () => {
        if (!user || !originalUserData) return;

        setIsSaving(true);
        setSaveError('');

        const promises = [];
        let generalUpdateNeeded = false;
        // Start with original data, but exclude fields handled by specific endpoints
        const generalUpdatePayload = {
            ...originalUserData,
            spins: undefined, // Exclude spins from general update
            userType: undefined, // Exclude userType from general update
            referralCode: undefined, // Exclude referral fields from general update
            referredByUserId: undefined,
            referralBonusClaimed: undefined,
            spinsFromReferrals: undefined,
        };

        // Check if trade URL changed
        if (formData.steamTradeUrl !== originalUserData.steamTradeUrl) {
            generalUpdatePayload.steamTradeUrl = formData.steamTradeUrl;
            generalUpdateNeeded = true;
        }
        // Add other general fields here if needed (e.g., displayName if editable)

        // Add general update promise if needed
        if (generalUpdateNeeded) {
            promises.push(updateUser(user.userId, generalUpdatePayload));
        }

        // Add spin update promise if spins changed
        if (formData.spins !== originalUserData.spins) {
            promises.push(updateUserSpins(user.userId, formData.spins));
        }

        // If no changes detected
        if (promises.length === 0) {
            setIsSaving(false);
            onClose(); // Just close if no changes
            return;
        }

        try {
            await Promise.all(promises);
            onSaveSuccess(); // Callback to refresh data in parent
            onClose(); // Close modal on success
        } catch (err) {
            const errMsg = err.response?.data?.title
                || err.response?.data?.message
                || err.message
                || 'Failed to save user changes.';
            setSaveError(errMsg);
            console.error("Save user error:", err.response || err);
        } finally {
            setIsSaving(false);
        }
    };

    if (!user) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Edit User: {user.displayName || user.steamId}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {saveError && <Alert severity="error">{saveError}</Alert>}
                    <TextField
                        label="User ID"
                        value={formData.userId || ''}
                        disabled fullWidth variant="outlined" size="small"
                    />
                    <TextField
                        label="Steam ID"
                        value={formData.steamId || ''}
                        disabled fullWidth variant="outlined" size="small"
                    />
                    <TextField
                        label="Display Name"
                        value={formData.displayName || ''}
                        disabled fullWidth variant="outlined" size="small"
                    />
                    <TextField
                        label="Steam Trade URL"
                        name="steamTradeUrl"
                        value={formData.steamTradeUrl || ''}
                        onChange={handleChange}
                        fullWidth variant="outlined" size="small"
                        placeholder="https://steamcommunity.com/tradeoffer/new/..."
                        disabled={isSaving}
                    />
                    <TextField
                        label="Spins"
                        name="spins"
                        type="number"
                        value={formData.spins}
                        onChange={handleChange}
                        fullWidth variant="outlined" size="small"
                        inputProps={{ min: 0 }}
                        disabled={isSaving}
                    />
                    <TextField
                        label="Referral Code"
                        value={formData.referralCode || 'N/A'}
                        disabled fullWidth variant="outlined" size="small"
                    />
                    <TextField
                        label="Referred By User ID"
                        value={formData.referredByUserId || 'N/A'}
                        disabled fullWidth variant="outlined" size="small"
                    />
                    <TextField
                        label="Referral Bonus Claimed"
                        value={formData.referralBonusClaimed ? 'Yes' : 'No'}
                        disabled fullWidth variant="outlined" size="small"
                    />
                    <TextField
                        label="Spins from Referrals"
                        type="number"
                        value={formData.spinsFromReferrals}
                        disabled fullWidth variant="outlined" size="small"
                    />
                    {/* UserType is now handled in the grid */}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} color="inherit" disabled={isSaving}>Cancel</Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                    disabled={isSaving}
                    startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// --- Main User Management Page Component ---
function UserManagementPage() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const { user: adminUser } = useAuth(); // Assuming useAuth provides the logged-in admin's info
    const { enqueueSnackbar } = useSnackbar();

    // --- NEW STATE: For Inventory Modal ---
    const [isInventoryModalOpen, setIsInventoryModalOpen] = useState(false);
    const [viewingInventoryUser, setViewingInventoryUser] = useState(null); // Store user whose inventory is being viewed
    // --- END NEW STATE ---

    // --- NEW: State for inline updates ---
    const [rowLoading, setRowLoading] = useState({}); // Track loading state per row, e.g., { userId: true }
    const [rowError, setRowError] = useState({});     // Track error state per row, e.g., { userId: 'Update failed' }
    // --- END NEW ---

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getAllUsers();
            // Ensure userType and referral fields are present, default if missing
            const processedData = (data || []).map(user => ({
                ...user,
                userType: user.userType ?? "",
                referralCode: user.referralCode || null,
                referredByUserId: user.referredByUserId || null,
                referralBonusClaimed: user.referralBonusClaimed || false,
                spinsFromReferrals: user.spinsFromReferrals || 0,
            }));
            const sortedData = processedData.sort((a, b) => a.userId - b.userId);
            setUsers(sortedData);
        } catch (err) {
            setError('Failed to load users. Please try refreshing.');
            console.error("Fetch users error:", err.response?.data || err.message || err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        document.title = 'SpinnerCS - Admin: Users';
        fetchUsers();
    }, [fetchUsers]);

    // --- Edit Modal Handlers ---
    const handleOpenEditModal = (userToEdit) => {
        setSelectedUser(userToEdit);
        setIsEditModalOpen(true);
    };

    const handleCloseEditModal = () => {
        setIsEditModalOpen(false);
        setSelectedUser(null);
    };

    const handleSaveSuccess = () => {
        enqueueSnackbar('User details saved successfully', { variant: 'success' });
        fetchUsers(); // Refresh the list after saving
    };

    // --- Delete Handler ---
    const handleDeleteUser = async (userId, userName) => {
        // Prevent deleting self
        if (adminUser && adminUser.userId === userId) {
            enqueueSnackbar("You cannot delete your own account.", { variant: 'warning' });
            return;
        }

        const confirmDelete = window.confirm(`Are you sure you want to delete user "${userName || userId}"? This action cannot be undone.`);
        if (confirmDelete) {
            setLoading(true); // Use main loading indicator for delete
            setError(null);
            try {
                await deleteUser(userId);
                enqueueSnackbar(`User ${userName || userId} deleted successfully`, { variant: 'success' });
                // Update local state immediately
                setUsers(prevUsers => prevUsers.filter(u => u.userId !== userId));
            } catch (err) {
                const message = err.response?.data?.message || err.message || `Failed to delete user ${userId}.`;
                setError(message);
                enqueueSnackbar(message, { variant: 'error' });
                console.error(`Delete user ${userId} error:`, err.response?.data || err.message || err);
            } finally {
                setLoading(false);
            }
        }
    };

    // --- NEW: Inventory Modal Handlers ---
    const handleOpenInventoryModal = (userToView) => {
        setViewingInventoryUser(userToView);
        setIsInventoryModalOpen(true);
    };

    const handleCloseInventoryModal = () => {
        setIsInventoryModalOpen(false);
        setViewingInventoryUser(null);
    };
    // --- END NEW HANDLERS ---

    // --- NEW: Handler for User Type Change ---
    const handleUserTypeChange = useCallback(async (userId, newUserType) => {
        setRowLoading(prev => ({ ...prev, [userId]: true }));
        setRowError(prev => ({ ...prev, [userId]: null }));
        try {
            await updateUserType(userId, newUserType);
            enqueueSnackbar('User type updated successfully', { variant: 'success' });
            // Update local state immediately for responsiveness
            setUsers(prevUsers =>
                prevUsers.map(user =>
                    user.userId === userId ? { ...user, userType: newUserType } : user
                )
            );
        } catch (err) {
            const message = err.response?.data?.message || err.message || 'Failed to update user type';
            console.error(`Error updating type for user ${userId}:`, err);
            setRowError(prev => ({ ...prev, [userId]: message }));
            enqueueSnackbar(message, { variant: 'error' });
            // Optionally revert local state change on error
            // fetchUsers(); // Or refetch to ensure consistency
        } finally {
            setRowLoading(prev => ({ ...prev, [userId]: false }));
        }
    }, [enqueueSnackbar]); // Removed fetchUsers dependency if not refetching
    // --- END NEW ---


    // --- Table Columns Definition ---
    const columns = useMemo(() => [
        {
            field: 'avatarUrl',
            headerName: 'Avatar',
            width: 80,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Avatar src={params.row.steamAvatarPicture} alt={params.row.displayName || '?'} sx={{ width: 32, height: 32 }} />
            ),
        },
        { field: 'userId', headerName: 'User ID', width: 90 },
        {
            field: 'steamId',
            headerName: 'Steam ID',
            width: 180,
            renderCell: (params) => (
                <Link
                    href={`https://steamcommunity.com/profiles/${params.value}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                >
                    {params.value}
                </Link>
            ),
        },
        { field: 'displayName', headerName: 'Display Name', width: 180, flex: 1 },
        { field: 'referralCode', headerName: 'Referral Code', width: 130, renderCell: (params) => params.value || '-' },
        { field: 'referredByUserId', headerName: 'Referred By ID', width: 130, renderCell: (params) => params.value || '-' },
        { field: 'referralBonusClaimed', headerName: 'Bonus Claimed', width: 130, align: 'center', headerAlign: 'center', renderCell: (params) => (params.value ? 'Yes' : 'No') },
        { field: 'spinsFromReferrals', headerName: 'Referral Spins', type: 'number', width: 130, align: 'right', headerAlign: 'right' },
        { field: 'spins', headerName: 'Spins', type: 'number', width: 90, align: 'right', headerAlign: 'right' },
        {
            field: 'steamTradeUrl',
            headerName: 'Trade URL Set',
            width: 130,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (params.value ? 'Yes' : 'No'),
        },
        // --- NEW: User Type Column ---
        {
            field: 'userType',
            headerName: 'User Type',
            width: 150,
            renderCell: (params) => {
                const userId = params.row.userId;
                const isLoading = rowLoading[userId];
                const errorMsg = rowError[userId];
                const currentValue = params.value ?? ""; // Default to empty string if null/undefined

                return (
                    <Tooltip title={errorMsg || ''} placement="top" arrow>
                        <FormControl fullWidth size="small" error={!!errorMsg} disabled={isLoading}>
                            <Select
                                value={currentValue}
                                onChange={(e) => handleUserTypeChange(userId, e.target.value)}
                                displayEmpty
                                inputProps={{ 'aria-label': 'User Type' }}
                                sx={{ height: 35 }} // Adjust height to fit row
                            >
                                <MenuItem value=""><em>Standard</em></MenuItem>
                                <MenuItem value="Staging">Staging</MenuItem>
                                <MenuItem value="Restricted">Restricted</MenuItem>
                                <MenuItem value="Bot">Bot</MenuItem>
                            </Select>
                            {isLoading && (
                                <CircularProgress size={20} sx={{ position: 'absolute', top: '50%', left: '50%', marginTop: '-10px', marginLeft: '-10px' }} />
                            )}
                        </FormControl>
                    </Tooltip>
                );
            },
        },
        // --- END NEW ---
        {
            field: 'createdAt',
            headerName: 'Created At',
            width: 180,
            valueGetter: (params) => formatDate(params.value),
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 150,
            sortable: false,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <>
                    <Tooltip title="View Inventory">
                        <IconButton onClick={() => handleOpenInventoryModal(params.row)} color="secondary" size="small" sx={{ mr: 0.5 }}>
                            <InventoryIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit User">
                        <IconButton onClick={() => handleOpenEditModal(params.row)} color="primary" size="small" sx={{ mr: 0.5 }}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete User">
                        <span> {/* Span needed for disabled button tooltip */}
                            <IconButton
                                onClick={() => handleDeleteUser(params.row.userId, params.row.displayName)}
                                color="error"
                                size="small"
                                disabled={loading || (adminUser && adminUser.userId === params.row.userId)} // Disable delete for self
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </>
            ),
        },
    ], [handleOpenEditModal, handleDeleteUser, handleOpenInventoryModal, handleUserTypeChange, rowLoading, rowError, loading, adminUser]); // Added dependencies


    // --- Render Logic ---
    if (loading && users.length === 0) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
    }

    return (
        <Box>
            {/* Header and Refresh Button */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>User Management ({users.length})</Typography>
                <Tooltip title="Refresh User List">
                    <span> {/* Span needed for disabled button tooltip */}
                        <IconButton onClick={fetchUsers} disabled={loading} color="primary">
                            <RefreshIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Table */}
            <TableContainer component={Paper}>
                {/* Using MUI Table instead of DataGrid for simpler implementation here */}
                <Table sx={{ minWidth: 1000 }} aria-label="user table" size="small">
                    <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 'bold' } }}>
                            {columns.map((col) => (
                                <TableCell
                                    key={col.field}
                                    align={col.align || 'left'}
                                    style={{ width: col.width }}
                                    sx={{ minWidth: col.width }}
                                >
                                    {col.headerName}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {users.map((user) => (
                            <TableRow
                                key={user.userId}
                                hover
                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                            >
                                {columns.map((col) => (
                                    <TableCell
                                        key={`${user.userId}-${col.field}`}
                                        align={col.align || 'left'}
                                    >
                                        {col.renderCell
                                            ? col.renderCell({ row: user, value: user[col.field], field: col.field })
                                            : col.valueGetter
                                                ? col.valueGetter({ value: user[col.field], row: user })
                                                : user[col.field] ?? ''}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Empty state message */}
            {users.length === 0 && !loading && (
                <Typography sx={{ mt: 3, textAlign: 'center', color: 'text.secondary' }}>
                    No users found.
                </Typography>
            )}

            {/* Edit Modal */}
            <UserEditModal
                open={isEditModalOpen}
                onClose={handleCloseEditModal}
                user={selectedUser}
                onSaveSuccess={handleSaveSuccess}
            />

            {/* --- NEW: Render Inventory Modal --- */}
            {viewingInventoryUser && (
                <UserInventoryModal
                    open={isInventoryModalOpen}
                    onClose={handleCloseInventoryModal}
                    userId={viewingInventoryUser.userId}
                    userName={viewingInventoryUser.displayName}
                />
            )}
            {/* --- END NEW --- */}
        </Box>
    );
}

export default UserManagementPage;
